import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Chip,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  Factory as PlantIcon,
  LocationOn as ZoneIcon,
  TableChart as TableIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';

import { getApiPlants, getApiZones, getApiTables } from '@/api/gen/api';
import TableSelector from './AutocompleteSelector/TableSelector';

export interface HierarchicalFilterSelections {
  plantId?: number;
  plantName?: string;
  zoneId?: number;
  zoneName?: string;
  tableId?: number;
  tableName?: string;
}

export interface HierarchicalFilterProps {
  levels: ('plant' | 'zone' | 'table')[];
  onChange: (selections: HierarchicalFilterSelections) => void;
  initialValues?: any;
  disabled?: boolean;
  size?: 'small' | 'medium';
  showPlant?: boolean;
  showZone?: boolean;
  showTable?: boolean;
  // If true, use the richer TableSelector component for the table level
  useTableSelector?: boolean;
}

const HierarchicalFilter: React.FC<HierarchicalFilterProps> = ({
  levels,
  onChange,
  size = 'medium',
  showPlant = true,
  showZone = true,
  showTable = true,
}) => {
  const [selectedPlant, setSelectedPlant] = useState<any>(null);
  const [selectedZone, setSelectedZone] = useState<any>(null);
  const [selectedTable, setSelectedTable] = useState<any>(null);

  const fetchAllPages = async <T extends Record<string, any>>(
    apiFn: (params: any) => Promise<any>,
    params: any
  ) => {
    const firstPage = await apiFn({ ...params, isPaginated: true, page: 1, pageSize: 10 });
    const totalCount = firstPage.totalCount || 0;
    const pageSize = firstPage.pageSize || 10;
    const totalPages = Math.ceil(totalCount / pageSize);

    if (totalPages <= 1) {
      return firstPage;
    }

    const remainingPages = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, i) =>
        apiFn({ ...params, isPaginated: true, page: i + 2, pageSize })
      )
    );

    return {
      ...firstPage,
      items: [
        ...(firstPage.items || []),
        ...remainingPages.flatMap(page => page.items || [])
      ]
    };
  };

  const { data: plantsResponse, isLoading: loadingPlants } = useQuery({
    queryKey: ['plants'],
    queryFn: () => fetchAllPages(getApiPlants, {}),
    enabled: showPlant,
  });

  const { data: zonesResponse, isLoading: loadingZones } = useQuery({
    queryKey: ['zones', selectedPlant?.id],
    queryFn: () => fetchAllPages(getApiZones, { PlantId: selectedPlant?.id }),
    enabled: !!selectedPlant?.id && showZone,
  });

  const { data: tablesResponse, isLoading: loadingTables } = useQuery({
    queryKey: ['tables', selectedZone?.id],
    queryFn: () => fetchAllPages(getApiTables, { ZoneId: selectedZone?.id }),
    enabled: !!selectedZone?.id && showTable,
  });

  const getSafeArray = (response: any): any[] => {
    if (!response) return [];

    if (response.items && Array.isArray(response.items)) {
      return response.items;
    }

    if (Array.isArray(response)) {
      return response;
    }

    if (response.data && Array.isArray(response.data)) {
      return response.data;
    }

    return [];
  };

  // Transformar datos
  const plants = useMemo(() => {
    if (!showPlant) return [];
    const dataArray = getSafeArray(plantsResponse);
    return dataArray.map((item: any) => ({
      id: item.id,
      name: item.plantName || item.name || `Planta ${item.id}`,
    }));
  }, [plantsResponse, showPlant]);

  const zones = useMemo(() => {
    if (!showZone) return [];
    const dataArray = getSafeArray(zonesResponse);
    return dataArray.map((item: any) => ({
      id: item.id,
      name: item.zoneName || item.name || `Zona ${item.id}`,
    }));
  }, [zonesResponse, showZone]);

  const tables = useMemo(() => {
    if (!showTable) return [];
    const dataArray = getSafeArray(tablesResponse);
    return dataArray.map((item: any) => {
      const name = item.tableName || item.name || `Tabla ${item.id}`;
      const uniqueKey = `${item.id}_${name}`;

      return {
        id: item.id,
        name: name,
        uniqueKey: uniqueKey,
      };
    });
  }, [tablesResponse, showTable]);

  // Handlers
  const handlePlantChange = (event: any, newValue: any) => {
    setSelectedPlant(newValue);
    setSelectedZone(null);
    setSelectedTable(null);
  };

  const handleZoneChange = (event: any, newValue: any) => {
    setSelectedZone(newValue);
    setSelectedTable(null);
  };


  useEffect(() => {
    const selections: HierarchicalFilterSelections = {
      plantId: selectedPlant?.id,
      zoneId: selectedZone?.id,
      plantName: selectedPlant?.name,
      zoneName: selectedZone?.name,
    };

    if (showTable) {
      selections.tableId = selectedTable?.id;
      selections.tableName = selectedTable?.name;
    }

    onChange(selections);
  }, [selectedPlant, selectedZone, selectedTable, onChange, showTable]);

  const clearSelection = () => {
    setSelectedPlant(null);
    setSelectedZone(null);
    setSelectedTable(null);

    // Al limpiar los filtros, se muestran todos los devices
    const emptySelections = {
      plantId: undefined,
      plantName: undefined,
      zoneId: undefined,
      zoneName: undefined,
      tableId: undefined,
      tableName: undefined,
    };
    onChange(emptySelections);
  };

  // Determinar qué niveles mostrar basado en los props showX
  const effectiveLevels = levels.filter(level => {
    switch (level) {
      case 'plant':
        return showPlant;
      case 'zone':
        return showZone;
      case 'table':
        return showTable;
      default:
        return false;
    }
  });

  // Calcular el tamaño de las columnas según cuántos filtros se muestran
  const getColumnSize = () => {
    const visibleCount = effectiveLevels.length;
    if (visibleCount === 1) return 12;
    if (visibleCount === 2) return 6;
    return 4; // 3 filtros
  };

  const columnSize = getColumnSize();

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      <Grid container spacing={2} alignItems='center'>
        {showPlant && effectiveLevels.includes('plant') && (
          <Grid item xs={12} sm={columnSize}>
            <Autocomplete
              size={size}
              options={plants}
              getOptionLabel={option => option.name}
              value={selectedPlant}
              onChange={handlePlantChange}
              loading={loadingPlants}
              getOptionKey={option => `${option.id}_${option.name}`}
              renderInput={params => (
                <TextField
                  {...params}
                  label={
                    <Box display='flex' alignItems='center'>
                      <PlantIcon sx={{ mr: 1, fontSize: 16 }} />
                      Planta
                    </Box>
                  }
                  placeholder='Seleccionar planta...'
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingPlants && <CircularProgress size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        )}

        {showZone && effectiveLevels.includes('zone') && (
          <Grid item xs={12} sm={columnSize}>
            <Autocomplete
              size={size}
              disabled={(!selectedPlant && showPlant) || loadingZones}
              options={zones}
              getOptionLabel={option => option.name}
              value={selectedZone}
              onChange={handleZoneChange}
              loading={loadingZones}
              getOptionKey={option => `${option.id}_${option.name}`}
              renderInput={params => (
                <TextField
                  {...params}
                  label={
                    <Box display='flex' alignItems='center'>
                      <ZoneIcon sx={{ mr: 1, fontSize: 16 }} />
                      Zona
                    </Box>
                  }
                  placeholder={
                    !selectedPlant && showPlant
                      ? 'Selecciona planta primero'
                      : 'Seleccionar zona...'
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingZones && <CircularProgress size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        )}

        {showTable && effectiveLevels.includes('table') && (
          <Grid item xs={12} sm={columnSize}>
            <TableSelector
              plantId={selectedPlant?.id}
              zoneId={selectedZone?.id}
              value={selectedTable?.id || null}
              onChange={tableId => {
                if (!tableId) {
                  setSelectedTable(null);
                  return;
                }
                // find matching table name from the tables list
                const found = tables.find((t: any) => t.id === tableId);
                setSelectedTable(
                  found
                    ? { id: found.id, name: found.name }
                    : { id: tableId, name: `Tabla ${tableId}` }
                );
              }}
              disabled={(!selectedZone && showZone) || loadingTables}
              label={'Tabla'}
              placeholder={
                !selectedZone && showZone
                  ? 'Selecciona zona primero'
                  : 'Seleccionar tabla...'
              }
            />
          </Grid>
        )}
      </Grid>

      {(selectedPlant || selectedZone || selectedTable) && (
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            Filtros activos:
          </Typography>
          {selectedPlant && showPlant && (
            <Chip
              icon={<PlantIcon />}
              label={selectedPlant.name}
              size='small'
              color='info'
              variant='outlined'
            />
          )}
          {selectedZone && showZone && (
            <Chip
              icon={<ZoneIcon />}
              label={selectedZone.name}
              size='small'
              color='secondary'
              variant='outlined'
            />
          )}
          {selectedTable && showTable && (
            <Chip
              icon={<TableIcon />}
              label={selectedTable.name}
              size='small'
              color='primary'
              variant='outlined'
            />
          )}
          <Chip
            label='Limpiar'
            size='small'
            variant='outlined'
            onClick={clearSelection}
            sx={{ cursor: 'pointer' }}
          />
        </Box>
      )}
    </Box>
  );
};

export default HierarchicalFilter;
